import {useMemo} from 'react'
import {Pencil} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {ErrorBanner} from '@/components/ui/error-banner'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {formatDateOnly} from '@/lib/date-utils'
import {useAttributeDefinitions} from '../hooks/useAttributeDefinitions'
import type {OnboardingFormState} from '../types'
import {OnboardingStep} from '../types'
import {renderDisplayNameTemplate, buildAttributeNameMap} from '@/lib/displayNameTemplate'
import type {TypeValueSettings} from '@/types/types'
import type {PartyRelationshipSchemaEntry} from '@/types/party'
import {Icon} from '@/components/shared/Icon'
import {MutedText} from '@/components/shared/MutedText'
import {SmallText} from '@/components/shared/SmallText'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {DetailList} from '@/components/shared/DetailList'
import {DiffRow} from '@/components/shared/DiffRow'
import {InlineRow} from '@/components/shared/InlineRow'

interface ReviewStepProps {
    form: OnboardingFormState
    onGoToStep: (step: number) => void
    submitError: Error | null
}

/* eslint-disable i18next/no-literal-string -- typed constants, not user-visible */
const DiffStyle = {
    NEW: 'new',
    CHANGED: 'changed',
    UNCHANGED: 'unchanged',
} as const
/* eslint-enable i18next/no-literal-string */
type DiffStyle = typeof DiffStyle[keyof typeof DiffStyle]

function getDiffStyle(isExisting: boolean, isNew: boolean, isChanged: boolean): DiffStyle {
    if (!isExisting) return DiffStyle.UNCHANGED // New party flow — no diff
    if (isNew) return DiffStyle.NEW
    if (isChanged) return DiffStyle.CHANGED
    return DiffStyle.UNCHANGED
}

/**
 * Step 4: Review — read-only summary with diff highlighting.
 */
export function ReviewStep({form, onGoToStep, submitError}: ReviewStepProps) {
    const i18n = useI18n()
    const {partyTypes, identityTypes, identifierTypes, partyRelationshipSchema} = useAppConfig()
    const isExisting = form.mode === 'existing' && !!form.existingPartyData

    const filledRelationships = form.relationships.filter(r => r.targetId)

    const {definitions: partyDefs} = useAttributeDefinitions('PARTY', form.partyType)

    // Compute the displayName the backend will produce, using the partyType's
    // displayNameTemplate and the attribute values the user has entered. In
    // `new` mode the backend derives displayName server-side; client-side
    // rendering gives the user a preview before submit. In `existing` mode
    // we keep the form value as-is (it was loaded from the existing party).
    const previewDisplayName = useMemo(() => {
        if (isExisting) return form.displayName
        const partyTypeValue = partyTypes.find(t => t.code === form.partyType)
        const template = (partyTypeValue?.settings as TypeValueSettings | undefined)?.displayNameTemplate
        const namedAttrs = buildAttributeNameMap(partyDefs, form.partyAttributes)
        return renderDisplayNameTemplate(template, namedAttrs) || form.displayName
    }, [isExisting, partyTypes, form.partyType, form.displayName, partyDefs, form.partyAttributes])

    return (
        <Stack>
            {submitError && <ErrorBanner error={submitError}/>}

            {/* Person Section */}
            <Card>
                <RowCardHeader className="pb-3">
                    <CardTitle className="font-semibold" data-testid="card-title-review-person">{i18n.onboarding.review.personSection}</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => onGoToStep(OnboardingStep.PERSON)} data-testid="review-edit-person">
                        <Icon icon={Pencil} size="xs" className="mr-1"/>
                        {i18n.onboarding.review.editLink}
                    </Button>
                </RowCardHeader>
                <CardContent>
                    <DetailList columns={1} className="gap-2">
                        {previewDisplayName && (
                            <ReviewItem
                                label={i18n.onboarding.person.displayName}
                                value={previewDisplayName}
                                diffStyle={getDiffStyle(
                                    isExisting,
                                    !isExisting,
                                    isExisting && previewDisplayName !== form.existingPartyData?.displayName
                                )}
                                oldValue={isExisting ? form.existingPartyData?.displayName : undefined}
                                isNew={!isExisting}
                                i18n={i18n}
                                data-testid="review-display-name"
                            />
                        )}
                        <ReviewItem
                            label={i18n.onboarding.existingParty.partyType}
                            value={labelOf(partyTypes, form.partyType)}
                            diffStyle={DiffStyle.UNCHANGED}
                            isNew={false}
                            i18n={i18n}
                        />
                    </DetailList>
                </CardContent>
            </Card>

            {/* Organization Section */}
            <Card data-testid="review-section-organization">
                <RowCardHeader className="pb-3">
                    <CardTitle className="font-semibold" data-testid="card-title-review-organization">{i18n.onboarding.review.organizationSection}</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => onGoToStep(OnboardingStep.ORGANIZATION)} data-testid="review-edit-organization">
                        <Icon icon={Pencil} size="xs" className="mr-1"/>
                        {i18n.onboarding.review.editLink}
                    </Button>
                </RowCardHeader>
                <CardContent>
                    <DetailList columns={1} className="gap-2">
                        {filledRelationships.map(rel => {
                            const schema = (partyRelationshipSchema ?? []).find(
                                (s: PartyRelationshipSchemaEntry) => s.relationshipType === rel.relationshipType
                            )
                            const label = schema?.fromSideLabel ?? rel.relationshipType
                            const existingRel = form.existingPartyData?.relationships?.find(
                                r => r.relationshipType === rel.relationshipType
                            )
                            return (
                                <ReviewItem
                                    key={rel.relationshipType}
                                    label={label}
                                    value={rel.targetLabel}
                                    diffStyle={getDiffStyle(
                                        isExisting,
                                        isExisting && !existingRel?.targetId && !!rel.targetId,
                                        isExisting && !!existingRel?.targetId && rel.targetId !== existingRel.targetId
                                    )}
                                    isNew={isExisting && !existingRel?.targetId && !!rel.targetId}
                                    i18n={i18n}
                                />
                            )
                        })}
                        {filledRelationships.length === 0 && (
                            <MutedText>{'—'}</MutedText>
                        )}
                    </DetailList>
                </CardContent>
            </Card>

            {/* Identity Section */}
            <Card data-testid="review-section-identity">
                <RowCardHeader className="pb-3">
                    <CardTitle className="font-semibold" data-testid="card-title-review-identity">{i18n.onboarding.review.identitySection}</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => onGoToStep(OnboardingStep.IDENTITY)} data-testid="review-edit-identity">
                        <Icon icon={Pencil} size="xs" className="mr-1"/>
                        {i18n.onboarding.review.editLink}
                    </Button>
                </RowCardHeader>
                <CardContent>
                    <DetailList columns={1} className="gap-2">
                        <ReviewItem
                            label={i18n.onboarding.review.identityTypeLabel}
                            value={labelOf(identityTypes, form.identityType)}
                            diffStyle={DiffStyle.NEW}
                            isNew={true}
                            i18n={i18n}
                        />
                        {form.entryDate && (
                            <ReviewItem
                                label={i18n.onboarding.review.entryDateLabel}
                                value={formatDateOnly(form.entryDate)}
                                diffStyle={DiffStyle.NEW}
                                isNew={true}
                                i18n={i18n}
                            />
                        )}
                        {form.exitDate && (
                            <ReviewItem
                                label={i18n.onboarding.review.exitDateLabel}
                                value={formatDateOnly(form.exitDate)}
                                diffStyle={DiffStyle.NEW}
                                isNew={true}
                                i18n={i18n}
                            />
                        )}
                    </DetailList>

                    {/* Identifiers */}
                    <Stack gap="xs" className="mt-3">
                        <MutedText size="xs" uppercase weight="medium">{i18n.onboarding.identity.identifiers}</MutedText>
                        {form.identifiers.map(row => (
                            <DiffRow key={row.id} diffStyle={DiffStyle.NEW}>
                                <Badge variant="outline">{labelOf(identifierTypes, row.identifierType)}</Badge>
                                <SmallText>{row.identifierValue}</SmallText>
                                {row.primary && <Badge variant="secondary">{i18n.onboarding.identity.primary}</Badge>}
                                <SmallText className="text-status-success">{i18n.onboarding.review.newLabel}</SmallText>
                            </DiffRow>
                        ))}
                    </Stack>
                </CardContent>
            </Card>

            {/* Attributes Section (if any entered) */}
            {(form.partyAttributes.length > 0 || form.identityAttributes.length > 0) && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="font-semibold" data-testid="card-title-review-attributes">{i18n.onboarding.review.attributesSection}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <DetailList columns={1} className="gap-2">
                            {form.partyAttributes.map((attr, i) => (
                                <DiffRow key={`p-${i}`} diffStyle={DiffStyle.NEW} layout="column">
                                    <MutedText>{attr.attributeName}: </MutedText>
                                    <SmallText>{attr.valueString ?? String(attr.valueNumber ?? attr.valueBoolean ?? '')}</SmallText>
                                </DiffRow>
                            ))}
                            {form.identityAttributes.map((attr, i) => (
                                <DiffRow key={`i-${i}`} diffStyle={DiffStyle.NEW} layout="column">
                                    <MutedText>{attr.attributeName}: </MutedText>
                                    <SmallText>{attr.valueString ?? String(attr.valueNumber ?? attr.valueBoolean ?? '')}</SmallText>
                                </DiffRow>
                            ))}
                        </DetailList>
                    </CardContent>
                </Card>
            )}
        </Stack>
    )
}

interface ReviewItemProps {
    label: string
    value: string
    diffStyle: DiffStyle
    oldValue?: string
    isNew: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    i18n: any
    'data-testid'?: string
}

function ReviewItem({label, value, diffStyle, oldValue, isNew, i18n, 'data-testid': testId}: ReviewItemProps) {
    return (
        <DiffRow diffStyle={diffStyle} data-testid={testId}>
            <MutedText className="w-32 shrink-0">{label}</MutedText>
            <InlineRow gap="md">
                {diffStyle === DiffStyle.CHANGED && oldValue && (
                    <MutedText className="line-through">{oldValue}</MutedText>
                )}
                <Strong>{value}</Strong>
                {diffStyle === DiffStyle.NEW && isNew && (
                    <SmallText className="text-status-success">{i18n.onboarding.review.newLabel}</SmallText>
                )}
            </InlineRow>
        </DiffRow>
    )
}
